import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { ContactPoint, ContactPointCategory } from '../../../../api/app-types/contact-point.app';
import { CreateContactPoint } from '../../../../api/app-types/relative.app';
import { UpdateUser, User } from '../../../../api/app-types/user.app';
import { SearchService } from '../../../../api/services/search.service';
import { USER_SERVICE } from '../../../../api/services/user.service';
import { AddressCompletion } from '../../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { formatDateToISOString, matDateProviders } from '../../../../shared/helpers/date.func';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { userExistsValidator } from '../../../../shared/validators/user-exist-validator';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../../shared/helpers/is-contact-point-complete.func';
import { TENANT_FEATURES } from '../../../../core/services/tenant-feature.service';

@Component({
	selector: 'lvr-admin-user-edit',
	imports: [
		LvrPaddingDirective,
		TranslocoPipe,
		LvrAppendAsteriskPipe,
		LvrField,
		LvrInputDirective,
		AddressCompletion,
		ReactiveFormsModule,
		MatInputModule,
		MatSelectModule,
		MatTooltipModule,
		MatButton,
		MatDatepickerModule,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrContactPoint,
		MatIcon,
		MatIconButton,
		LvrDefaultActions,
		LvrButton,
	],
	templateUrl: './admin-user-edit.html',
	providers: [SearchService, matDateProviders],
})
export class AdminUserEdit implements OnInit {
	private readonly env = inject(ENVIRONMENT);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly routeData = toSignal(this.route.data);
	private readonly emailRegex = new RegExp(this.env.emailRegexStr);
	private readonly nameRegex = new RegExp(this.env.nameRegexStr);
	private readonly titleRegex = new RegExp(this.env.titleRegexStr);
	private readonly searchApi = inject(SearchService);
	private readonly draftService = inject(FormPersistenceService);
	private readonly userService = inject(USER_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	public readonly showSpecialization = computed(() => this.tenantFeatures.specializationEnabled());

	public readonly form = this.initForm();
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly gracePeriod = this.env.gracePeriodYears;
	public readonly maxDate = signal(new Date());
	public readonly minDate = computed(() => {
		const max = this.maxDate();
		const min = new Date(max);
		min.setFullYear(max.getFullYear() - this.gracePeriod);
		return min;
	});
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly showPositionShareInputs = signal(false);
	public readonly draftId = computed(() => {
		return `${FORM_KEYS.UPDATE_USER}.${this.user()?.id}`;
	});
	public readonly hasError = hasError;
	public readonly canEditUsersPermission = hasPermission(this.session.permissions, 'Users.Edit');
	public readonly user = computed(() => this.routeData()?.['user'] as User);
	public readonly genders = computed(() => this.routeData()?.['genders'] ?? []);
	public readonly roles = computed(() => this.routeData()?.['roles'] ?? []);
	public readonly integrationOffices = computed(() => this.routeData()?.['integrationOffices'] ?? []);
	public readonly specializations = computed(() => this.routeData()?.['specializations'] ?? []);
	public readonly contactPointCategories = computed(
		() => (this.routeData()?.['contactPointCategories'].entities || []) as ContactPointCategory[],
	);
	public readonly loading = signal(false);

	public get contactPoints(): ContactPoint[] {
		return (this.form.get('baseInfo.contactPoints')?.value ?? []) as ContactPoint[];
	}

	public async ngOnInit() {
		this.initializeFormFromUser();
		this.updatePositionShareValidators();

		const u = this.user();
		const emailCtrl = this.form.get('baseInfo.businessEmail');
		emailCtrl?.setAsyncValidators(
			userExistsValidator(this.searchApi, { originalEmail: u?.email, excludeObjectId: u?.id }),
		);
		emailCtrl?.updateValueAndValidity({ emitEvent: false });
	}

	public addContactPoint() {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const cp: ContactPoint = {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
		values.push(cp);
		this.form.get('baseInfo.contactPoints')?.patchValue(values);
	}

	public removeContactPoint(index: number) {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			this.form.get('baseInfo.contactPoints')?.patchValue(values);
		}
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value); // Update same item in array
		} else {
			values.push(data.value); // Add if new
		}
	}

	public addPositionShare() {
		if (this.showPositionShareInputs()) return;
		this.showPositionShareInputs.set(true);
		this.form.get('employment.start')?.reset(undefined);
		this.form.get('employment.positionShare')?.reset(undefined);
		this.updatePositionShareValidators();
	}

	/**
	 * Cancels the edit mode and discards any unsaved changes.
	 * Deletes the saved draft and emits a cancel event to the parent.
	 */
	public handleCancelEdit() {
		this.draftService.deleteDraft('user', this.draftId());
		this.router.navigate(['../../'], { relativeTo: this.route });
	}

	/**
	 * Validates the form, emits the UpdateUser object, and clears the saved draft
	 */
	public async onSubmit() {
		if (this.loading()) return;
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.loading.set(true);

		const fv = this.form.value;
		const useNewPositionShare = this.showPositionShareInputs();
		const startDate = useNewPositionShare ? fv.employment?.start : this.user().positionShare?.start;
		const shareValue = useNewPositionShare ? fv.employment?.positionShare : this.user().positionShare?.share;
		const body: UpdateUser = {
			// Personal info
			title: fv.baseInfo?.title ?? '',
			firstName: fv.baseInfo?.firstName ?? '',
			name: fv.baseInfo?.name ?? '',
			businessEmail: fv.baseInfo?.businessEmail ?? '',
			notesOnAccessibility: '',
			genderId: fv.baseInfo?.genderId ?? undefined,
			address: {
				postalCodeAndCity: fv.baseInfo?.address?.['postalCodeAndCity'] ?? '',
				streetAndHouseNumber: fv.baseInfo?.address?.['streetAndHouseNumber'] ?? '',
			},
			remark: '',
			contactPoints: [],

			// Job info
			employmentId: fv.employment?.employmentId ?? -1,
			roleId: this.user().roles?.id ?? '',
			positionShare: shareValue ?? -1,
			start: formatDateToISOString(startDate) ?? '',
			end: undefined,
			specializationId: fv.employment?.specializationId ?? -1,
			integrationServiceOfficeId: this.user().integrationServiceOffice!.id,
		};

		const filteredCps = (fv.baseInfo.contactPoints ?? []).filter(isContactPointComplete);

		if (filteredCps.length > 0) {
			const additionalCps: CreateContactPoint[] = filteredCps.map((cp: CreateContactPoint) => ({
				id: cp.id === -1 ? undefined : cp.id,
				preferredWayOfContact: cp.preferredWayOfContact,
				categoryContactPointId: cp.categoryContactPointId,
				contactValue: cp.contactValue?.trim(),
				remark: cp.remark?.trim(),
			}));

			body.contactPoints!.push(...additionalCps);
		}

		try {
			await this.userService.update(this.user().objectId, body);
			this.router.navigate(['..'], { relativeTo: this.route });
			this.notificationService.addNotification(createSuccessNotification('Benutzer aktualisiert'));
			this.draftService.deleteDraft('user', this.draftId());
		} catch {
			this.notificationService.addNotification(createErrorNotification('Fehler beim Aktualisieren'));
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * Initializes the user form the user data provided via API.
	 */
	private initializeFormFromUser() {
		const user = this.user();
		if (!user) {
			console.warn('No user available');
			return;
		}

		this.form.patchValue(this.mapUserToForm(user));
	}

	/**
	 * Maps the current `User` object to the form structure.
	 */
	private mapUserToForm(u: User) {
		return {
			baseInfo: {
				title: u.title,
				firstName: u.firstName ?? '',
				name: u.lastName ?? '',
				businessEmail: u.email ?? '',
				genderId: u.genderId,
				address: {
					streetAndHouseNumber: u.streetAddress ?? '',
					postalCodeAndCity: this.joinPostalCity(u.postalCode, u.city),
				},
				contactPoints: u.contactPoints,
			},
			employment: {
				start: u.positionShare?.start ?? undefined,
				positionShare: u.positionShare?.share ?? undefined,
				roleId: u.roles?.id,
				serviceId: u.service?.id,
				employmentId: u.employment?.id,
				specializationId: u.specialization?.id,
				integrationServiceOfficeId: u.integrationServiceOffice?.id,
			},
		};
	}

	/**
	 * Joins postal code and city into a single line.
	 */
	private joinPostalCity = (postal?: string, city?: string) =>
		[postal ?? '', city ?? ''].filter(Boolean).join(' ') || '';

	/**
	 * Builds the form and sets validators.
	 */
	private initForm(): FormGroup {
		const fb = inject(FormBuilder);

		const baseInfo = fb.group({
			title: ['', [Validators.pattern(this.titleRegex)]],
			firstName: ['', [Validators.required, Validators.pattern(this.nameRegex)]],
			name: ['', [Validators.required, Validators.pattern(this.nameRegex)]],
			businessEmail: fb.control<string>('', {
				validators: [Validators.required, Validators.pattern(this.emailRegex)],
				asyncValidators: [userExistsValidator(this.searchApi)],
				updateOn: 'change',
			}),
			genderId: fb.control<number | undefined>(undefined, [Validators.required]),
			address: [undefined],
			contactPoints: [[] as ContactPoint[]],
		});

		const employment = fb.group({
			start: [undefined],
			positionShare: [undefined, [Validators.min(0), Validators.max(1)]],
			roleId: [undefined, [Validators.required]],
			specializationId: [undefined, this.showSpecialization() ? [Validators.required] : []],
			serviceId: [undefined, [Validators.required]],
			employmentId: fb.control<number | undefined>(undefined, [Validators.required]),
			integrationServiceOfficeId: fb.control<number | undefined>(undefined, [Validators.required]),
		});

		const formGroup = fb.group({
			baseInfo,
			employment,
		});

		return formGroup;
	}

	private updatePositionShareValidators() {
		const startCtrl = this.form.get('employment.start');
		const shareCtrl = this.form.get('employment.positionShare');
		if (!startCtrl || !shareCtrl) return;

		if (this.showPositionShareInputs()) {
			startCtrl.setValidators([Validators.required]);
			shareCtrl.setValidators([Validators.required, Validators.min(0), Validators.max(1)]);
		} else {
			startCtrl.setValidators([]);
			shareCtrl.setValidators([Validators.min(0), Validators.max(1)]);
		}
		startCtrl.updateValueAndValidity({ emitEvent: false });
		shareCtrl.updateValueAndValidity({ emitEvent: false });
	}
}
